/**
 * LearnVocab.jsx — the word-recognition-automaticity drill. User skims
 * the passage briefly, then hits a 3-card vocab deck where each card
 * offers 4 definition choices. Because perceptual-span growth is
 * driven by automatic word recognition, this drill compounds long-
 * term speed gains even though it doesn't feel like a "speed" drill.
 *
 * Scores against the passage's `vocab` bank on the server (server-
 * side answer key stays hidden, same as the comprehension bank).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Type as TypeIcon, Play, ArrowLeft, ChevronDown, StopCircle } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnVocab = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [state, setState] = useState('setup'); // setup|reading|deck|done
  const startedAt = useRef(0);
  const durationRef = useRef(0);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) { setPassages([custom]); return; }
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/learn/passages`);
        if (r.ok) { const d = await r.json(); setPassages(d.passages || []); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];

  const start = () => { startedAt.current = Date.now(); setState('reading'); };
  const toDeck = () => { durationRef.current = Date.now() - startedAt.current; setState('deck'); };
  const reset = () => { setState('setup'); durationRef.current = 0; };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-vocab-loading">
        Loading passages…
      </main>
    );
  }

  const hasVocab = Array.isArray(passage.vocab) && passage.vocab.length > 0;

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-vocab-page">
      <button onClick={() => navigate('/learn')} className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <TypeIcon className="w-6 h-6 text-amber-300" />
        <div>
          <p className="eyebrow text-amber-300 mb-0.5">Drill · Vocabulary reps</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            Compound long-term<span className="text-amber-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-amber-400/60"
                data-testid="learn-vocab-passage-select"
              >
                {passages.map((p, i) => (<option key={p.passage_id} value={i}>{p.title} — {p.author} ({p.word_count} words)</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <p className="text-xs text-white/50 leading-relaxed">
            Skim the passage. Then a {passage.vocab?.length || 3}-card vocab deck asks you to pick the meaning of the trickier words you just saw. Word-recognition automaticity is the quiet lever behind long-term speed gains.
          </p>
          {!hasVocab && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 px-3 py-2 text-xs text-amber-200">
              This passage has no vocab bank yet — pick a different one.
            </div>
          )}
          <button
            onClick={start}
            disabled={!hasVocab}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-vocab-start"
          >
            <Play className="w-4 h-4" /> Skim then deck
          </button>
        </div>
      )}

      {state === 'reading' && (
        <div className="space-y-4">
          <p className="eyebrow text-amber-300 flex items-center gap-1.5">
            Skim now — {passage.vocab?.length || 3} vocab cards after
          </p>
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-amber-500/20 p-6 text-white/90 leading-relaxed text-base sm:text-lg" data-testid="learn-vocab-passage-text">
            {passage.text}
          </div>
          <button
            onClick={toDeck}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-vocab-to-deck"
          >
            <StopCircle className="w-4 h-4" /> Ready — start deck
          </button>
        </div>
      )}

      {state === 'deck' && (
        <LearnQuiz
          passage={passage}
          drillKind="vocab"
          durationMs={durationRef.current}
          eyebrow="Vocab deck"
          questions={passage.vocab}
          onDone={() => setState('done')}
        />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button onClick={reset} className="flex-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 px-4 py-3 text-sm font-black" data-testid="learn-vocab-again">
            Run another drill
          </button>
          <button onClick={() => navigate('/learn')} className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black">
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnVocab;
